import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldValues, optionLabel, type FilterField } from './filters';

/** Closes on a click outside and on Escape (returning the focus to the trigger), while open. */
function useDismissOutside(
  open: boolean,
  containerRef: RefObject<HTMLDivElement | null>,
  triggerRef: RefObject<HTMLButtonElement | null>,
  dismiss: () => void,
) {
  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        containerRef.current !== null &&
        !containerRef.current.contains(event.target)
      ) {
        dismiss();
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        dismiss();
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, containerRef, triggerRef, dismiss]);
}

function FilterMenuBody({
  field,
  fields,
  active,
  panelId,
  onPick,
  onChoose,
  onToggle,
}: {
  readonly field: FilterField | null;
  readonly fields: readonly FilterField[];
  readonly active: Readonly<Record<string, string | null>>;
  readonly panelId: string;
  readonly onPick: (field: FilterField | null) => void;
  readonly onChoose: (key: string, value: string) => void;
  /** A `multiple` field's ticks: the field's new comma-separated value, empty for none. */
  readonly onToggle: (key: string, value: string) => void;
}) {
  const { t } = useTranslation('common');
  return (
    <>
      {field === null ? (
        <>
          <p className="filter-menu-caption">{t('list.filterBy')}</p>
          {fields.map((candidate) => {
            const current = active[candidate.key];
            return (
              <button
                key={candidate.key}
                type="button"
                className="filter-menu-field"
                onClick={() => onPick(candidate)}
              >
                <span>{candidate.label}</span>
                <span className="filter-menu-current">
                  {current ? optionLabel(candidate, current) : null}
                  <ChevronRight aria-hidden="true" className="size-3.5" />
                </span>
              </button>
            );
          })}
        </>
      ) : (
        <fieldset className="filter-menu-options">
          <legend className="sr-only">{field.label}</legend>
          <button type="button" className="filter-menu-back" onClick={() => onPick(null)}>
            <ChevronLeft aria-hidden="true" className="size-3.5" />
            {field.label}
          </button>
          {/* A long list scrolls inside the menu, the back button staying in view. */}
          <div className="filter-menu-scroll">
            {field.multiple ? (
              <MultipleOptions field={field} active={active} onToggle={onToggle} />
            ) : (
              field.options.map((option) => (
                <label
                  key={option.value}
                  className="filter-menu-option"
                  data-indent={option.indent ? '' : undefined}
                >
                  <input
                    type="radio"
                    name={`${panelId}-${field.key}`}
                    value={option.value}
                    defaultChecked={active[field.key] === option.value}
                    onChange={() => onChoose(field.key, option.value)}
                  />
                  {option.label}
                </label>
              ))
            )}
          </div>
        </fieldset>
      )}
    </>
  );
}

/** A `multiple` field's options: each a checkbox, ticked while its value is in force. */
function MultipleOptions({
  field,
  active,
  onToggle,
}: {
  readonly field: FilterField;
  readonly active: Readonly<Record<string, string | null>>;
  readonly onToggle: (key: string, value: string) => void;
}) {
  const chosen = fieldValues(field, active[field.key]);
  return (
    <>
      {field.options.map((option) => {
        const checked = chosen.includes(option.value);
        return (
          <label
            key={option.value}
            className="filter-menu-option"
            data-indent={option.indent ? '' : undefined}
          >
            <input
              type="checkbox"
              value={option.value}
              checked={checked}
              onChange={() =>
                onToggle(
                  field.key,
                  (checked
                    ? chosen.filter((value) => value !== option.value)
                    : [...chosen, option.value]
                  ).join(','),
                )
              }
            />
            {option.label}
          </label>
        );
      })}
    </>
  );
}

/**
 * «+ Filtro» (decision 101, the #405 boards): choose the field, then its value, as Stripe's «Add
 * filter» does. Choosing a value applies it and closes the menu, with the focus back on the button;
 * a `multiple` field's checkboxes apply each tick and leave the menu open for the next.
 *
 * A disclosure, not an ARIA menu, for the reasons `AccountMenu` gives: a short list of buttons and
 * radios needs Escape, a click outside and focus restoration, not a menu bar's arrow-key model.
 * No popover library: none is installed, and this is forty lines.
 */
export function AddFilterMenu({
  fields,
  active,
  onChoose,
}: {
  readonly fields: readonly FilterField[];
  readonly active: Readonly<Record<string, string | null>>;
  readonly onChoose: (key: string, value: string) => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [field, setField] = useState<FilterField | null>(null);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  function close() {
    setOpen(false);
    setField(null);
    triggerRef.current?.focus();
  }

  const dismiss = useCallback(() => {
    setOpen(false);
    setField(null);
  }, []);
  useDismissOutside(open, containerRef, triggerRef, dismiss);

  return (
    <div className="filter-add" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="filter-add-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((current) => !current);
          setField(null);
        }}
      >
        <Plus aria-hidden="true" className="size-3.5" />
        {t('list.addFilter')}
      </button>
      {open ? (
        <div id={panelId} role="group" aria-label={t('list.menuLabel')} className="filter-menu">
          <FilterMenuBody
            field={field}
            fields={fields}
            active={active}
            panelId={panelId}
            onPick={setField}
            onChoose={(key, value) => {
              onChoose(key, value);
              close();
            }}
            onToggle={onChoose}
          />
        </div>
      ) : null}
    </div>
  );
}
